import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #explicitColorHandle = '';

  #findMatchingMediaGallery(source) {
    if (!(source instanceof Element) && !(source instanceof Document)) return null;

    const sectionRoot = this.closest('.shopify-section[id]');
    if (sectionRoot?.id) {
      const matchingGallery = source.querySelector(`#${CSS.escape(sectionRoot.id)} media-gallery`);
      if (matchingGallery instanceof HTMLElement) return matchingGallery;
    }

    const featuredProduct = this.closest('featured-product-information');
    if (featuredProduct?.id) {
      const matchingGallery = source.querySelector(`#${CSS.escape(featuredProduct.id)} media-gallery`);
      if (matchingGallery instanceof HTMLElement) return matchingGallery;
    }

    const productInformationMedia = this.closest('.product-information__media');
    if (productInformationMedia) {
      const productInformationRoot = this.closest('[data-testid="product-information"]');
      const matchingRoot = productInformationRoot?.closest('.shopify-section[id]')
        ? source.querySelector(`#${CSS.escape(productInformationRoot.closest('.shopify-section[id]').id)} .product-information__media media-gallery`)
        : source.querySelector('.product-information__media media-gallery');
      if (matchingRoot instanceof HTMLElement) return matchingRoot;
    }

    const quickAddContent = this.closest('.quick-add-modal__content, #quick-add-modal-content');
    if (quickAddContent) {
      const matchingGallery = source.querySelector('.quick-add-modal__content media-gallery, #quick-add-modal-content media-gallery');
      if (matchingGallery instanceof HTMLElement) return matchingGallery;
    }

    const matchingGallery = source.querySelector('media-gallery');
    return matchingGallery instanceof HTMLElement ? matchingGallery : null;
  }

  /**
   * 'primary' | 'secondary' on dual-gallery bundle pages, '' otherwise.
   */
  get bundlePart() {
    return this.dataset.bundleGalleryPart || '';
  }

  #isHiddenBundleGallery() {
    return Boolean(this.bundlePart) && (this.hidden || Boolean(this.closest('[hidden]')));
  }

  connectedCallback() {
    super.connectedCallback();
    // console.log('[media-gallery] connected', this.#getDebugInfo());
    this.#bindGlobalColorSwatchTracking();
    this.#bindLocalZoomClickHandling();
    this.#bindLocalThumbnailClickHandling();
    // A hidden bundle gallery only runs the pure-DOM color filter; layout work
    // (slide select, rail sync) and the shared skeleton reveal are deferred to
    // activateBundleGallery() at first unhide — its reveal fallback timer would
    // otherwise flip the shared wrapper before the visible gallery is ready.
    const deferLayoutInit = this.#isHiddenBundleGallery();
    let initialTargetMediaId = '';
    try {
      initialTargetMediaId = this.#focusSelectedColorMedia();
    } finally {
      // Always reveal, even if color-focus threw, so the wrapper never gets
      // stuck on the 2.5s CSS/setTimeout failsafe.
      if (!deferLayoutInit) this.#revealMediaWrapperWhenReady();
    }
    if (!deferLayoutInit) {
      if (this.bundlePart) this.dataset.bundleActivated = 'true';
      if (initialTargetMediaId) {
        this.#selectSlideshowMediaWithRetry(initialTargetMediaId);
        this.#syncDesktopRailToMediaId(initialTargetMediaId);
      }
      this.#refreshThumbnailControllers();
    }

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener('variant-picker:state-sync', this.#handleVariantPickerStateSync, { signal });
    target?.addEventListener('bonobos:gallery-sync', this.#handleBonobosGallerySync, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
  #explicitBundlePart = '';
  #explicitFeaturedMediaId = '';
  #slideshowSelectTimeouts = [];
  #scheduledColorMediaSyncRaf = null;
  #scheduledBonobosGallerySyncRaf = null;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#clearSlideshowSelectTimeouts();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Dual-gallery bundle pages: a fetched replacement gallery would arrive
    // without the part attribute/visibility and destroy the dual setup. Part
    // galleries are updated via bonobos:gallery-sync / replaceGalleryForPart.
    if (this.bundlePart) return;

    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
    requestAnimationFrame(() => {
      if (newMediaGallery instanceof HTMLElement && typeof newMediaGallery.refreshThumbnailControllers === 'function') {
        newMediaGallery.refreshThumbnailControllers();
      }
    });
  };

  #handleVariantPickerStateSync = () => {
    this.#scheduleSelectedColorMediaSync();
  };

  #scheduleSelectedColorMediaSync() {
    if (this.#scheduledColorMediaSyncRaf != null) {
      cancelAnimationFrame(this.#scheduledColorMediaSyncRaf);
    }
    this.#scheduledColorMediaSyncRaf = requestAnimationFrame(() => {
      this.#scheduledColorMediaSyncRaf = null;
      this.#syncSelectedColorMedia();
    });
  }

  #syncSelectedColorMedia() {
    const targetMediaId = this.#focusSelectedColorMedia();
    // Hidden bundle gallery: keep it filtered (pure DOM work above) but defer
    // slide/rail sync — it has zero layout until first unhide.
    if (this.#isHiddenBundleGallery()) return;
    if (targetMediaId) {
      this.#selectSlideshowMediaWithRetry(targetMediaId);
      this.#syncDesktopRailToMediaId(targetMediaId);
      return;
    }

    this.#selectFirstVisibleSlideshowMediaWithRetry();
  }

  #handleBonobosGallerySync = (event) => {
    const part = event?.detail?.part || '';
    // Both part galleries listen on the same section; ignore syncs meant for
    // the other part or one gallery gets filtered by the other's color.
    if (this.bundlePart && part && this.bundlePart !== part) return;
    const isQuickAddGallery = Boolean(this.closest('quick-add-dialog, #quick-add-modal-content, .quick-add-modal__content'));
    const featuredMediaId =
      !isQuickAddGallery && event?.detail?.featuredMediaId ? String(event.detail.featuredMediaId) : '';
    const colorHandle = this.#normalizeHandle(event?.detail?.colorHandle || '');

    if (part === 'primary' || part === 'secondary') {
      this.#explicitBundlePart = part;
    }
    this.#explicitFeaturedMediaId = featuredMediaId;
    this.#explicitColorHandle = colorHandle || '';

    this.#scheduleBonobosGallerySync(isQuickAddGallery);
  };

  #scheduleBonobosGallerySync(isQuickAddGallery) {
    if (this.#scheduledBonobosGallerySyncRaf != null) {
      cancelAnimationFrame(this.#scheduledBonobosGallerySyncRaf);
    }
    this.#scheduledBonobosGallerySyncRaf = requestAnimationFrame(() => {
      this.#scheduledBonobosGallerySyncRaf = null;
      this.#runBonobosGallerySync(isQuickAddGallery);
    });
  }

  #runBonobosGallerySync(isQuickAddGallery) {
    const colorHandle = this.#explicitColorHandle;
    const targetMediaId = this.#focusSelectedColorMedia();
    if (this.#isHiddenBundleGallery()) return;
    if (targetMediaId) {
      this.#selectSlideshowMediaWithRetry(targetMediaId);
      this.#syncDesktopRailToMediaId(targetMediaId);
      return;
    }

    if (isQuickAddGallery || colorHandle) {
      this.#selectFirstVisibleSlideshowMediaWithRetry();
    } else if (this.#explicitFeaturedMediaId) {
      this.#selectSlideshowMediaWithRetry(this.#explicitFeaturedMediaId);
      this.#syncDesktopRailToMediaId(this.#explicitFeaturedMediaId);
    }
  }

  /**
   * Reveals the surrounding media wrapper (skeleton -> gallery) once the
   * gallery is connected, color-filtered, and its first visible image has
   * loaded. The wrapper shows a shimmer skeleton while data-media-ready is
   * 'false' (see product-information-layout-styles.liquid), with a CSS
   * failsafe reveal at 2.5s.
   */
  #revealMediaWrapperWhenReady() {
    const wrapper = this.closest('.product-information__media[data-media-ready]');
    if (!(wrapper instanceof HTMLElement) || wrapper.dataset.mediaReady === 'true') return;

    let settled = false;
    const reveal = () => {
      if (settled) return;
      settled = true;
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          wrapper.dataset.mediaReady = 'true';
        })
      );
    };

    const firstVisibleImage = this.querySelector(
      '.media-gallery__grid > [data-media-id]:not([hidden]) img, slideshow-slide[data-media-id]:not([hidden]) img'
    );

    if (firstVisibleImage instanceof HTMLImageElement && !firstVisibleImage.complete) {
      firstVisibleImage.addEventListener('load', reveal, { once: true });
      firstVisibleImage.addEventListener('error', reveal, { once: true });
      // Track so disconnectedCallback cancels it — otherwise a gallery swapped
      // out mid-load leaves an orphaned timer that flips the shared wrapper.
      this.#slideshowSelectTimeouts.push(window.setTimeout(reveal, 1200));
    } else {
      reveal();
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  static #globalColorSwatchTrackingBound = false;

  #getDebugInfo() {
    const section = this.closest('.shopify-section, dialog');
    const mediaIds = Array.from(this.querySelectorAll('[data-media-id]'))
      .map((node) => node.getAttribute('data-media-id'))
      .filter(Boolean);

    return {
      presentation: this.dataset.presentation || '',
      sectionTag: section?.tagName?.toLowerCase() || '',
      sectionId: section?.id || '',
      galleryClass: this.className || '',
      mediaNodeCount: mediaIds.length,
      uniqueMediaIdCount: new Set(mediaIds).size,
    };
  }

  #bindGlobalColorSwatchTracking() {
    if (MediaGallery.#globalColorSwatchTrackingBound) return;

    const captureSwatch = (event) => {
      if (!(event.target instanceof Element)) return;

      let colorValue = '';
      let bundleStepFromStep = '';
      let bundleStepFromPicker = '';
      let scopeElement = null;

      const piv2ColorSwatch = event.target.closest(
        '.piv2-option-group[data-option-handle="color"] .piv2-swatch-btn, .piv2-option-group[data-option-handle="colour"] .piv2-swatch-btn'
      );
      if (piv2ColorSwatch instanceof HTMLButtonElement) {
        colorValue = piv2ColorSwatch.dataset.valueName || piv2ColorSwatch.getAttribute('aria-label') || '';
        bundleStepFromStep = piv2ColorSwatch.closest('[data-bundle-step]')?.getAttribute('data-bundle-step') || '';
        bundleStepFromPicker = piv2ColorSwatch.closest('variant-picker-v2')?.getAttribute('data-bundle-part') || '';
        scopeElement = piv2ColorSwatch;
      } else {
        const colorLabel = event.target.closest(
          'fieldset[data-option-name-handle="color"] label.variant-option__button-label'
        );
        const colorInput =
          event.target.closest('fieldset[data-option-name-handle="color"] input[type="radio"]') ||
          colorLabel?.querySelector('input[type="radio"]');

        if (!(colorInput instanceof HTMLInputElement)) return;

        colorValue = colorInput.value || colorInput.getAttribute('aria-label') || '';
        bundleStepFromStep = colorInput.closest('[data-bundle-step]')?.getAttribute('data-bundle-step') || '';
        bundleStepFromPicker =
          colorInput.closest('variant-picker-v2')?.getAttribute('data-bundle-part') ||
          colorInput.closest('variant-picker')?.getAttribute('data-bundle-part') ||
          '';
        scopeElement = colorInput;
      }

      const colorHandle = this.#normalizeHandle(colorValue);

      const bundleStep =
        bundleStepFromStep === 'primary' || bundleStepFromStep === 'secondary'
          ? bundleStepFromStep
          : bundleStepFromPicker;
      if (bundleStep === 'primary' || bundleStep === 'secondary') {
        window.__bonobosBundleLastColorValue = window.__bonobosBundleLastColorValue || {};
        window.__bonobosBundleLastColorValue[bundleStep] = colorValue;

        const scope = scopeElement?.closest('.shopify-section, dialog') || document;
        const bundlePickerRoot = scope.querySelector('[data-bundle-picker]');
        if (bundlePickerRoot instanceof HTMLElement) {
          bundlePickerRoot.dataset.activeStepPart = bundleStep;
          if (colorHandle) {
            bundlePickerRoot.dataset.activeColorHandle = colorHandle;
          }
        }
      }

      window.__bonobosLastColorValue = colorValue;

      // console.log('[media-gallery] captured global swatch interaction', {
      //   eventType: event.type,
      //   bundleStep,
      //   value: colorValue,
      // });
    };

    document.addEventListener('pointerdown', captureSwatch, true);
    document.addEventListener('click', captureSwatch, true);
    document.addEventListener('input', captureSwatch, true);

    MediaGallery.#globalColorSwatchTrackingBound = true;
  }

  #bindLocalZoomClickHandling() {
    this.#removeDelegatedZoomClickAttributes();
    this.addEventListener('click', this.#handleLocalZoomClick, {
      capture: true,
      signal: this.#controller.signal,
    });
  }

  #bindLocalThumbnailClickHandling() {
    this.addEventListener('click', this.#handleLocalThumbnailClick, {
      capture: true,
      signal: this.#controller.signal,
    });
  }

  #isDesktopViewport() {
    return window.matchMedia('(min-width: 1024px)').matches;
  }

  #getTopScrollOffset() {
    const rootStyles = getComputedStyle(document.body);
    const headerGroupVar = Number.parseFloat(rootStyles.getPropertyValue('--header-group-height'));
    if (!Number.isNaN(headerGroupVar) && headerGroupVar > 0) return headerGroupVar;

    const headerGroup = document.querySelector('#header-group');
    if (headerGroup instanceof HTMLElement) {
      const groupHeight = headerGroup.getBoundingClientRect().height;
      if (groupHeight > 0) return groupHeight;
    }

    const header = document.querySelector('header-component');
    if (header instanceof HTMLElement) {
      return header.getBoundingClientRect().height;
    }

    return 0;
  }

  #scrollToGridMediaById(mediaId) {
    const targetId = String(mediaId || '');
    if (!targetId) return;

    const grid = this.querySelector('.media-gallery__desktop-main .media-gallery__grid');
    if (!(grid instanceof HTMLElement)) return;

    const target = grid.querySelector(`:scope > [data-media-id="${CSS.escape(targetId)}"]`);
    if (!(target instanceof HTMLElement)) return;
    if (target.hidden || target.style.display === 'none' || target.getAttribute('aria-hidden') === 'true') return;

    const topOffset = this.#getTopScrollOffset();
    const targetTop = target.getBoundingClientRect().top + window.scrollY - topOffset;
    window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
  }

  #handleLocalThumbnailClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const thumb = event.target.closest(
      '.media-gallery__mobile-controls .slideshow-control[data-media-id], [data-rail-thumb][data-media-id]'
    );
    if (!(thumb instanceof HTMLElement) || !this.contains(thumb)) return;
    if (thumb.hidden || thumb.style.display === 'none' || thumb.getAttribute('aria-hidden') === 'true') return;

    const mediaId = thumb.getAttribute('data-media-id') || '';
    if (!mediaId) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const isRailThumb = thumb.hasAttribute('data-rail-thumb');
    if (isRailThumb && this.presentation === 'grid' && this.#isDesktopViewport()) {
      this.#scrollToGridMediaById(mediaId);
      this.#syncDesktopRailToMediaId(mediaId);
      return;
    }

    this.#selectSlideshowMediaWithRetry(mediaId);
    this.#syncDesktopRailToMediaId(mediaId);
    this.#setMobileControlActiveMediaId(mediaId);
    this.#syncMobileThumbIntoView(thumb);
  };

  #syncMobileThumbIntoView(thumb) {
    const container = thumb.closest('.slideshow-controls__thumbnails-container');
    if (!(container instanceof HTMLElement)) return;

    const targetLeft = thumb.offsetLeft - Math.max(0, (container.clientWidth - thumb.offsetWidth) / 2);
    const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);

    container.scrollTo({
      left: Math.max(0, Math.min(maxScrollLeft, targetLeft)),
      behavior: 'smooth',
    });
  }

  #removeDelegatedZoomClickAttributes() {
    this.querySelectorAll('[on\\:click*="zoom-dialog"][on\\:click*="/open/"]').forEach((element) => {
      element.removeAttribute('on:click');
    });
  }

  #handleLocalZoomClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('zoom-dialog')) return;
    if (event.target.closest('button, a, input, select, textarea, summary, [role="button"]')) return;

    const mediaNode = event.target.closest(
      'slideshow-slide[data-media-id], .media-gallery__grid > [data-media-id]'
    );
    if (!(mediaNode instanceof HTMLElement) || !this.contains(mediaNode)) return;
    if (mediaNode.hidden || mediaNode.style.display === 'none' || mediaNode.getAttribute('aria-hidden') === 'true') return;

    const mediaIndex = this.#resolveZoomIndexForMediaNode(mediaNode);
    if (mediaIndex < 0) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.zoom(mediaIndex, /** @type {PointerEvent} */ (event));
  };

  #resolveZoomIndexForMediaNode(mediaNode) {
    const mediaId = mediaNode.getAttribute('data-media-id') || '';
    if (mediaId && Array.isArray(this.refs.media)) {
      const refIndex = this.refs.media.findIndex((item) => item.getAttribute('data-media-id') === mediaId);
      if (refIndex >= 0) return refIndex;
    }

    const rawIndex = mediaNode.getAttribute('data-media-index');
    const parsedIndex = rawIndex != null ? Number.parseInt(rawIndex, 10) : NaN;
    return Number.isFinite(parsedIndex) ? parsedIndex : -1;
  }

  #normalizeHandle(value = '') {
    return value
      .toString()
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // Shopify deduplicates filename collisions by appending a UUID before the
  // extension (e.g. `foo_3_category_9ba84584-e2e9-4426-8232-28875bdc9a13.jpg`).
  // The image_color_group metafield lists the clean names, so strip that suffix
  // from the normalized handle on both sides so the collision-renamed media
  // still match. See BSM image-color-group filtering.
  #stripUploadSuffix(handle = '') {
    return handle.replace(/-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, '');
  }

  #normalizeImageFilename(value = '') {
    const filename = value
      .toString()
      .split('/')
      .pop()
      ?.split('?')[0]
      ?.split('#')[0] || '';
    return this.#stripUploadSuffix(this.#normalizeHandle(filename.replace(/\.[^.]+$/, '')));
  }

  #getImageColorGroupMap() {
    const rawValue = this.dataset.imageColorGroup || '';
    if (!rawValue) return null;

    try {
      const parsed = JSON.parse(rawValue);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

      const map = new Map();
      Object.entries(parsed).forEach(([colorName, group]) => {
        const colorHandle = this.#normalizeHandle(colorName);
        const images = Array.isArray(group?.images) ? group.images : [];
        if (!colorHandle || !images.length) return;

        const imageHandles = images.map((image) => this.#normalizeImageFilename(image)).filter(Boolean);
        if (imageHandles.length) {
          map.set(colorHandle, new Set(imageHandles));
        }
      });

      return map.size ? map : null;
    } catch {
      return null;
    }
  }

  #getImageColorGroupMatchingMediaIds(colorHandle = '') {
    const groupMap = this.#getImageColorGroupMap();
    const selectedColorHandle = this.#normalizeHandle(colorHandle);
    if (!groupMap || !selectedColorHandle) return [];

    const imageHandles = groupMap.get(selectedColorHandle);
    if (!imageHandles?.size) return [];

    const matchingIds = [];
    const seenIds = new Set();
    const nodes = Array.from(this.querySelectorAll('[data-media-id]'));

    nodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      const mediaId = node.getAttribute('data-media-id');
      if (!mediaId || seenIds.has(mediaId)) return;

      const fileHandle = this.#stripUploadSuffix(this.#normalizeHandle(node.dataset.mediaFileHandle || ''));
      if (!fileHandle || !imageHandles.has(fileHandle)) return;

      seenIds.add(mediaId);
      matchingIds.push(mediaId);
    });

    return matchingIds;
  }

  // BSM-1403: shorts sold in several inseams carry a photo set per length, and
  // the length is the number right before the first `_` after the style code
  // (`..._BSM10930N1246B-32-5_1_<uuid>.jpg` is the 5" set). Images without a
  // length token are global and show for every length; with no length picked,
  // the regular 7" set leads. Read from the <img> src rather than a Liquid
  // attribute so the Storefront rebuild after a sibling swap is covered too.
  static #LENGTH_OPTION_HANDLE = 'short-length';
  static #DEFAULT_LENGTH_TOKEN = '7';

  #lengthToken(value = '') {
    return value.toString().match(/\d+(?:\.\d+)?/)?.[0] || '';
  }

  #mediaLengthToken(src = '', lengthTokens) {
    const filename = src.split('/').pop()?.split('?')[0]?.replace(/\.[^.]+$/, '') || '';
    for (const segment of filename.split('_')) {
      const token = segment.match(/-(\d+(?:\.\d+)?)$/)?.[1];
      // Only an offered length counts, so a waist-only `-32` stays global.
      if (token) return lengthTokens.has(token) ? token : '';
    }
    return '';
  }

  #getLengthMatchingMediaIds() {
    if (this.bundlePart) return [];
    const section = this.closest('.shopify-section, dialog') || document;
    const group = section.querySelector(
      `variant-picker-v2 .piv2-option-group[data-option-handle="${MediaGallery.#LENGTH_OPTION_HANDLE}"]`
    );
    if (!(group instanceof HTMLElement)) return [];

    const lengthTokens = new Set(
      Array.from(group.querySelectorAll('.piv2-swatch-btn[data-value-name]'))
        .map((btn) => this.#lengthToken(btn instanceof HTMLElement ? btn.dataset.valueName : ''))
        .filter(Boolean)
    );
    if (!lengthTokens.size) return [];

    // Before the picker hydrates nothing carries `.selected`, so fall back to
    // the URL param it writes (`short-length=5 In`).
    const selectedBtn = group.querySelector('.piv2-swatch-btn.selected');
    let selectedToken = this.#lengthToken(
      (selectedBtn instanceof HTMLElement && selectedBtn.dataset.valueName) ||
        new URL(window.location.href).searchParams.get(MediaGallery.#LENGTH_OPTION_HANDLE) ||
        ''
    );
    if (!lengthTokens.has(selectedToken)) {
      selectedToken = lengthTokens.has(MediaGallery.#DEFAULT_LENGTH_TOKEN) ? MediaGallery.#DEFAULT_LENGTH_TOKEN : '';
    }
    if (!selectedToken) return [];

    /** @type {Map<string, { index: number, token: string }>} */
    const mediaById = new Map();
    this.querySelectorAll('[data-media-id]').forEach((node) => {
      const mediaId = node.getAttribute('data-media-id');
      const img = node.querySelector('img');
      if (!mediaId || mediaById.has(mediaId) || !(img instanceof HTMLImageElement)) return;
      mediaById.set(mediaId, {
        index: Number(node.getAttribute('data-media-index') ?? mediaById.size),
        token: this.#mediaLengthToken(img.getAttribute('src') || '', lengthTokens),
      });
    });

    const media = Array.from(mediaById, ([id, item]) => ({ id, ...item })).sort((a, b) => a.index - b.index);
    if (!media.some((item) => item.token)) return [];

    const globalIds = media.filter((item) => !item.token).map((item) => item.id);
    const lengthIds = media.filter((item) => item.token === selectedToken).map((item) => item.id);
    if (lengthIds.length) {
      return media.filter((item) => !item.token || item.token === selectedToken).map((item) => item.id);
    }

    // No photos for this length: lead with the featured image.
    return [media[0].id, ...globalIds.filter((id) => id !== media[0].id)];
  }

  #showOnlyMediaIds(mediaIds) {
    // Assigned videos belong to every color and length. Share one ordered set.
    mediaIds = [...new Set([...mediaIds.map(String), ...(this.dataset.sharedVideoIds || '').split(',').filter(Boolean)])];
    const targetMediaId = mediaIds[0];
    this.#setNoColorMediaMatchState(false);
    this.#filterMediaToIds(mediaIds);
    this.#reorderDesktopGridToMediaIds(mediaIds);
    this.#forceSlideshowToMediaId(targetMediaId);
    this.#refreshZoomDialog(targetMediaId);
    return targetMediaId;
  }

  #resolveSelectedColorHandle() {
    if (this.#explicitColorHandle) return this.#explicitColorHandle;

    // A part-bound gallery resolves color only from its own part's sources;
    // the globals below track the ACTIVE part and would filter the hidden
    // gallery with the other product's color.
    if (this.bundlePart) return this.#resolveBundlePartColorHandle(this.bundlePart);

    const bundlePickerRoot = this.closest('.shopify-section, dialog')?.querySelector('[data-bundle-picker]');
    const activePart = bundlePickerRoot?.dataset.activeStepPart || '';
    const datasetColorHandle = this.#normalizeHandle(bundlePickerRoot?.dataset.activeColorHandle || '');
    if (datasetColorHandle) return datasetColorHandle;

    if (activePart === 'primary' || activePart === 'secondary') {
      const scopedColorHandle = this.#normalizeHandle(window.__bonobosBundleLastColorValue?.[activePart] || '');
      if (scopedColorHandle) return scopedColorHandle;
    }

    const globalColorHandle = this.#normalizeHandle(window.__bonobosLastColorValue || '');
    if (globalColorHandle) return globalColorHandle;

    const section = this.closest('.shopify-section, dialog') || document;
    const picker = section.querySelector('variant-picker');
    if (picker instanceof HTMLElement) {
      const checkedColorInput = picker.querySelector(
        'fieldset[data-option-name-handle="color"] input[type="radio"]:checked, fieldset[data-option-name-handle="color"] input[type="radio"][data-current-checked="true"]'
      );
      if (checkedColorInput instanceof HTMLInputElement) {
        return this.#normalizeHandle(checkedColorInput.value || checkedColorInput.getAttribute('aria-label') || '');
      }

      const colorSelect = picker.querySelector('select[data-option-name-handle="color"]');
      const selectedColorOption = colorSelect?.selectedOptions?.[0];
      if (selectedColorOption instanceof HTMLOptionElement) {
        return this.#normalizeHandle(selectedColorOption.value || '');
      }
    }

    const piv2Picker =
      activePart === 'primary' || activePart === 'secondary'
        ? section.querySelector(`[data-bundle-step="${activePart}"] variant-picker-v2`)
        : section.querySelector('variant-picker-v2');
    if (piv2Picker instanceof HTMLElement) {
      const selectedSwatch = piv2Picker.querySelector(
        '.piv2-option-group[data-option-handle="color"] .piv2-swatch-btn.selected, .piv2-option-group[data-option-handle="colour"] .piv2-swatch-btn.selected'
      );
      if (selectedSwatch instanceof HTMLElement) {
        return this.#normalizeHandle(
          selectedSwatch.dataset.valueName || selectedSwatch.getAttribute('aria-label') || ''
        );
      }
    }

    return '';
  }

  /**
   * Color handle for a specific bundle part, ignoring active-part globals.
   * @param {string} part - 'primary' | 'secondary'
   */
  #resolveBundlePartColorHandle(part) {
    const section = this.closest('.shopify-section, dialog') || document;
    const bundlePickerRoot = section.querySelector('[data-bundle-picker]');

    // Written by bundle-picker's setActiveGalleryStateFromControl /
    // setBundlePartColorHandle as `${part}LastColorHandle`.
    const partColorHandle = this.#normalizeHandle(bundlePickerRoot?.dataset[`${part}LastColorHandle`] || '');
    if (partColorHandle) return partColorHandle;

    if (bundlePickerRoot?.dataset.activeStepPart === part) {
      const activeColorHandle = this.#normalizeHandle(bundlePickerRoot?.dataset.activeColorHandle || '');
      if (activeColorHandle) return activeColorHandle;
    }

    const scopedColorHandle = this.#normalizeHandle(window.__bonobosBundleLastColorValue?.[part] || '');
    if (scopedColorHandle) return scopedColorHandle;

    const partPicker = section.querySelector(`[data-bundle-step="${part}"] variant-picker-v2`);
    if (partPicker instanceof HTMLElement) {
      const selectedSwatch = partPicker.querySelector(
        '.piv2-option-group[data-option-handle="color"] .piv2-swatch-btn.selected, .piv2-option-group[data-option-handle="colour"] .piv2-swatch-btn.selected'
      );
      if (selectedSwatch instanceof HTMLElement) {
        return this.#normalizeHandle(
          selectedSwatch.dataset.valueName || selectedSwatch.getAttribute('aria-label') || ''
        );
      }
    }

    return '';
  }

  #setNoColorMediaMatchState(isNoMatch) {
    if (!this.dataset.originalHintClass) {
      this.dataset.originalHintClass = this.classList.contains('media-gallery--hint') ? 'true' : 'false';
    }

    this.dataset.noColorMediaMatch = isNoMatch ? 'true' : 'false';
    this.classList.toggle('media-gallery--hint', !isNoMatch && this.dataset.originalHintClass === 'true');
  }

  #orderGridItems(grid, items) {
    // Shared players keep their relative order. Move the surrounding images
    // without disconnecting those players and cancelling their media listeners.
    const sharedIds = new Set((this.dataset.sharedVideoIds || '').split(',').filter(Boolean));
    let anchor = null;
    for (const item of items.slice().reverse()) {
      if (!sharedIds.has(item.getAttribute('data-media-id')) && item.nextSibling !== anchor) {
        grid.insertBefore(item, anchor);
      }
      anchor = item;
    }
  }

  #resetDesktopGridOrder() {
    const grid = this.querySelector('.media-gallery__desktop-main .media-gallery__grid');
    if (!(grid instanceof HTMLElement)) return;

    const mediaItems = Array.from(grid.querySelectorAll(':scope > [data-media-id]'));
    if (!mediaItems.length) return;

    const orderedItems = [...mediaItems].sort(
      (a, b) => Number(a.getAttribute('data-media-index') || 0) - Number(b.getAttribute('data-media-index') || 0)
    );

    orderedItems.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      item.hidden = false;
      item.style.display = '';
      item.setAttribute('aria-hidden', 'false');
      item.classList.remove('is-color-match');
    });

    this.#orderGridItems(grid, orderedItems);
    this.#resetDesktopRailOrder();
    this.#refreshThumbnailControllers();
  }

  #resetMediaFiltering() {
    const mediaNodes = Array.from(
      this.querySelectorAll(
        'slideshow-slide[data-media-id], .media-gallery__grid > [data-media-id], [data-rail-thumb][data-media-id], .media-gallery__mobile-controls .slideshow-control[data-media-id], .media-gallery__mobile-controls .slideshow-controls__dots > li[data-media-id], zoom-dialog .dialog-zoomed-gallery > [data-media-id], zoom-dialog [ref="thumbnails"] > [data-media-id]'
      )
    );

    mediaNodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      node.hidden = false;
      node.style.display = '';
      node.setAttribute('aria-hidden', 'false');
      node.classList.remove('is-color-match');
    });

    this.#resetDesktopGridOrder();
    this.#syncSingleMediaState(new Set(mediaNodes.map((node) => node.getAttribute('data-media-id'))).size);
  }

  #resetDesktopRailOrder() {
    const railTrack = this.querySelector('[data-rail-track]');
    if (!(railTrack instanceof HTMLElement)) return;

    const thumbs = Array.from(railTrack.querySelectorAll(':scope > [data-rail-thumb]'));
    if (!thumbs.length) return;

    const orderedThumbs = [...thumbs].sort(
      (a, b) => Number(a.getAttribute('data-media-index') || 0) - Number(b.getAttribute('data-media-index') || 0)
    );
    const currentOrder = thumbs.map((thumb) => thumb.getAttribute('data-media-id')).join(',');
    const nextOrder = orderedThumbs.map((thumb) => thumb.getAttribute('data-media-id')).join(',');
    if (currentOrder === nextOrder) return;

    const fragment = document.createDocumentFragment();
    orderedThumbs.forEach((thumb) => {
      if (thumb instanceof HTMLElement) {
        thumb.hidden = false;
        thumb.style.display = '';
        thumb.setAttribute('aria-hidden', 'false');
        thumb.classList.remove('is-color-match');
      }
      fragment.appendChild(thumb);
    });
    railTrack.appendChild(fragment);
  }

  // On a combined listing without an explicit image_color_group mapping, never
  // colour-filter: each child is one colour, so its media already IS that
  // colour and the filename/alt matcher has nothing legitimate to select. Left
  // to run, it latches onto whatever happens to be named after the colour —
  // observed on 5 blazer/suit-jacket PDPs (2026-09-14) where the fabric-swatch
  // tile `navy_<uuid>.jpg` became the ONLY visible media after a sibling swap
  // back, because the Storefront rebuild strips Shopify's upload UUID from the
  // file handle (mediaFileHandle) while Liquid keeps it, so the tile only
  // matches on the rebuilt nodes. Store-wide the matcher matches nothing on
  // first paint (no colour-named files without a UUID, no colour alt text), so
  // gating CL pages out changes nothing shoppers see today except the bug.
  #shouldShowAllMediaForSelectedProduct() {
    if (this.#getImageColorGroupMap()) return false;
    const section = this.closest('.shopify-section, dialog, product-card') || document;

    // v1 picker — legacy combined-listing markers, unchanged.
    const v1Picker = section.querySelector(
      'variant-picker[data-combined-listing-parent-id], variant-picker[data-combined-listing-options="true"]'
    );
    if (v1Picker instanceof HTMLElement) return true;

    // v2 picker, same breadth as v1: `data-cl-role` is the analogue of
    // `data-combined-listing-options` (any CL page, parent or child) and
    // `data-combined-listing="true"` of `data-combined-listing-parent-id` (a
    // child whose combined_listing_parent_id is set) — both emitted by
    // snippets/variant-picker-v2.liquid. Scoped to a bundle part so one CL
    // part cannot gate the other part's gallery: this gallery's own part in
    // dual-gallery mode, else the bundle picker's ACTIVE step for the shared
    // single gallery (the same source #resolveSelectedColorHandle reads). A
    // part with no step container gates nothing.
    const activePart =
      section.querySelector('[data-bundle-picker]')?.dataset.activeStepPart || '';
    const part =
      this.bundlePart ||
      (activePart === 'primary' || activePart === 'secondary' ? activePart : '');
    const scope = part ? section.querySelector(`[data-bundle-step="${part}"]`) : section;
    return Boolean(
      scope?.querySelector(
        'variant-picker-v2[data-cl-role], variant-picker-v2[data-combined-listing="true"]'
      )
    );
  }

  #focusSelectedColorMedia() {
    const lengthMediaIds = this.#getLengthMatchingMediaIds();
    // Narrow a color match to the selected length, keeping the color match
    // whole if the two share no media.
    const narrowToLength = (mediaIds) => {
      const narrowed = mediaIds.filter((mediaId) => lengthMediaIds.includes(mediaId));
      return narrowed.length ? narrowed : mediaIds;
    };

    if (this.#shouldShowAllMediaForSelectedProduct()) {
      if (lengthMediaIds.length) return this.#showOnlyMediaIds(lengthMediaIds);
      this.#setNoColorMediaMatchState(false);
      this.#resetMediaFiltering();
      this.#refreshZoomDialog();
      return '';
    }

    const colorHandle = this.#resolveSelectedColorHandle();
    const explicitFeaturedMediaId = this.#explicitFeaturedMediaId ? String(this.#explicitFeaturedMediaId) : '';
    if (!colorHandle && !explicitFeaturedMediaId) {
      if (lengthMediaIds.length) return this.#showOnlyMediaIds(lengthMediaIds);
      // console.log('[media-gallery] no color handle, resetting filter', this.#getDebugInfo());
      this.#setNoColorMediaMatchState(false);
      this.#resetMediaFiltering();
      this.#refreshZoomDialog();
      return '';
    }

    const nodes = Array.from(this.querySelectorAll('[data-media-id]'));
    if (!nodes.length) return '';

    const imageColorGroupMatchingMediaIds = this.#getImageColorGroupMatchingMediaIds(colorHandle);
    if (imageColorGroupMatchingMediaIds.length) {
      return this.#showOnlyMediaIds(narrowToLength(imageColorGroupMatchingMediaIds));
    }

    /** @type {Map<string, Set<string>>} */
    const fileHandlesByMediaId = new Map();

    nodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      const mediaId = node.getAttribute('data-media-id');
      if (!mediaId) return;

      if (!fileHandlesByMediaId.has(mediaId)) {
        fileHandlesByMediaId.set(mediaId, new Set());
      }

      const fileHandle = this.#normalizeHandle(node.dataset.mediaFileHandle || '');
      const altHandle = this.#normalizeHandle(node.dataset.mediaAltHandle || '');

      if (fileHandle) fileHandlesByMediaId.get(mediaId).add(fileHandle);
      if (altHandle) fileHandlesByMediaId.get(mediaId).add(altHandle);
    });

    const candidates = [colorHandle].filter(Boolean);
    const matchingMediaIds = Array.from(fileHandlesByMediaId.entries())
      .filter(([, handles]) =>
        Array.from(handles).some((handle) => candidates.includes(handle))
      )
      .map(([mediaId]) => mediaId);
    if (
      explicitFeaturedMediaId &&
      !colorHandle &&
      fileHandlesByMediaId.has(explicitFeaturedMediaId) &&
      !matchingMediaIds.includes(explicitFeaturedMediaId)
    ) {
      matchingMediaIds.unshift(explicitFeaturedMediaId);
    }
    const targetMediaId = matchingMediaIds[0];

    // console.log('[media-gallery] filter evaluation', {
    //   ...this.#getDebugInfo(),
    //   targetMediaId: targetMediaId || '',
    //   media: Array.from(fileHandlesByMediaId.entries()).map(([mediaId, handles]) => ({
    //     mediaId,
    //     handles: Array.from(handles),
    //     matches: matchingMediaIds.includes(mediaId),
    //   })),
    // });

    if (!targetMediaId) {
      if (lengthMediaIds.length) return this.#showOnlyMediaIds(lengthMediaIds);
      this.#setNoColorMediaMatchState(true);
      this.#resetMediaFiltering();
      this.#refreshZoomDialog();
      return '';
    }

    return this.#showOnlyMediaIds(narrowToLength(matchingMediaIds));
  }

  #filterMediaToIds(mediaIds) {
    const matchingIds = new Set(mediaIds.map((mediaId) => String(mediaId)));
    const firstMatchingId = mediaIds.length ? String(mediaIds[0]) : '';
    const mediaNodes = Array.from(
      this.querySelectorAll(
        'slideshow-slide[data-media-id], .media-gallery__grid > [data-media-id], [data-rail-thumb][data-media-id], .media-gallery__mobile-controls .slideshow-control[data-media-id], .media-gallery__mobile-controls .slideshow-controls__dots > li[data-media-id], zoom-dialog .dialog-zoomed-gallery > [data-media-id], zoom-dialog [ref="thumbnails"] > [data-media-id]'
      )
    );
    const shown = [];
    const hidden = [];

    mediaNodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      const mediaId = node.getAttribute('data-media-id');
      const isMatch = mediaId ? matchingIds.has(String(mediaId)) : false;

      node.hidden = !isMatch;
      node.style.display = isMatch ? '' : 'none';
      node.setAttribute('aria-hidden', String(!isMatch));
      node.classList.toggle('is-color-match', isMatch);
      if (
        node.matches(
          '.media-gallery__mobile-controls .slideshow-control[data-media-id], .media-gallery__mobile-controls .slideshow-controls__dots > li[data-media-id]'
        )
      ) {
        node.setAttribute('aria-selected', String(isMatch && mediaId === firstMatchingId));
      }

      const row = {
        tag: node.tagName.toLowerCase(),
        mediaId: mediaId || '',
        fileHandle: node.dataset.mediaFileHandle || '',
        altHandle: node.dataset.mediaAltHandle || '',
      };

      if (isMatch) shown.push(row);
      else hidden.push(row);
    });

    // console.log('[media-gallery] filter applied', {
    //   ...this.#getDebugInfo(),
    //   matchingMediaIds: Array.from(matchingIds),
    //   shown,
    //   hidden,
    // });

    this.#syncSingleMediaState(new Set(shown.map((row) => row.mediaId)).size);
    this.#refreshThumbnailControllers({ immediate: true });
  }

  /**
   * A length filter can leave one visible image. Give it the layout Liquid
   * renders for a one-image product (`is_single_media`): full width, no rail,
   * no arrows or pagination; and undo it when more images come back.
   * @param {number} visibleCount
   */
  #syncSingleMediaState(visibleCount) {
    // Quick view keeps its carousel for one image too, so the drawer does not
    // jump in height as the visible count changes.
    if (this.closest('quick-add-dialog, #quick-add-modal-content, .quick-add-modal__content')) return;
    const isSingle = visibleCount <= 1;
    this.classList.toggle('media-gallery--single-media', isSingle);

    const slideshow = this.querySelector('slideshow-component');
    slideshow?.classList.toggle('product-media-gallery__slideshow--single-media', isSingle);
    slideshow?.classList.toggle('slideshow--single-media', isSingle);

    this.querySelectorAll('slideshow-controls, slideshow-arrows').forEach((control) => {
      if (!(control instanceof HTMLElement) || control.closest('zoom-dialog')) return;
      if (isSingle && !control.hidden) {
        control.hidden = true;
        control.style.setProperty('display', 'none', 'important');
        control.dataset.singleMediaHidden = 'true';
      } else if (!isSingle && control.dataset.singleMediaHidden) {
        control.hidden = false;
        control.style.removeProperty('display');
        delete control.dataset.singleMediaHidden;
      }
    });
  }

  #syncMainMediaToVisibleRail() {
    const visibleRailIds = Array.from(this.querySelectorAll('[data-rail-thumb][data-media-id]'))
      .filter(
        (thumb) =>
          thumb instanceof HTMLElement &&
          !thumb.hidden &&
          thumb.style.display !== 'none' &&
          thumb.getAttribute('aria-hidden') !== 'true'
      )
      .map((thumb) => thumb.getAttribute('data-media-id'))
      .filter(Boolean);

    if (!visibleRailIds.length) return;

    const visibleIds = new Set(visibleRailIds.map((mediaId) => String(mediaId)));
    const mainMediaNodes = Array.from(
      this.querySelectorAll('slideshow-slide[data-media-id], .media-gallery__grid > [data-media-id]')
    );

    mainMediaNodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      const mediaId = node.getAttribute('data-media-id');
      const shouldShow = mediaId ? visibleIds.has(String(mediaId)) : false;

      node.hidden = !shouldShow;
      node.style.display = shouldShow ? '' : 'none';
      node.setAttribute('aria-hidden', String(!shouldShow));
      node.classList.toggle('is-color-match', shouldShow);
    });
  }

  #reorderDesktopGridToMediaIds(mediaIds) {
    const grid = this.querySelector('.media-gallery__desktop-main .media-gallery__grid');
    if (!(grid instanceof HTMLElement)) return;

    const mediaItems = Array.from(grid.querySelectorAll(':scope > [data-media-id]'));
    if (!mediaItems.length) return;

    const matchingIds = new Set(mediaIds.map((mediaId) => String(mediaId)));
    const orderedItems = [...mediaItems].sort((a, b) => {
      const aMatch = matchingIds.has(a.getAttribute('data-media-id'));
      const bMatch = matchingIds.has(b.getAttribute('data-media-id'));

      if (aMatch !== bMatch) return aMatch ? -1 : 1;

      return Number(a.getAttribute('data-media-index') || 0) - Number(b.getAttribute('data-media-index') || 0);
    });

    const currentOrder = mediaItems.map((item) => item.getAttribute('data-media-id')).join(',');
    const nextOrder = orderedItems.map((item) => item.getAttribute('data-media-id')).join(',');

    orderedItems.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      const isMatch = matchingIds.has(item.getAttribute('data-media-id'));
      item.hidden = !isMatch;
      item.style.display = isMatch ? '' : 'none';
      item.setAttribute('aria-hidden', String(!isMatch));
      item.classList.toggle('is-color-match', isMatch);
    });

    if (currentOrder !== nextOrder) {
      this.#orderGridItems(grid, orderedItems);
    }

    this.#reorderDesktopRailToMediaIds(mediaIds);
    this.#syncMainMediaToVisibleRail();
    this.#refreshThumbnailControllers({ immediate: true });

  }

  #reorderDesktopRailToMediaIds(mediaIds) {
    const railTrack = this.querySelector('[data-rail-track]');
    if (!(railTrack instanceof HTMLElement)) return;

    const thumbs = Array.from(railTrack.querySelectorAll(':scope > [data-rail-thumb]'));
    if (!thumbs.length) return;

    const matchingIds = new Set(mediaIds.map((mediaId) => String(mediaId)));
    const orderedThumbs = [...thumbs].sort((a, b) => {
      const aMatch = matchingIds.has(a.getAttribute('data-media-id'));
      const bMatch = matchingIds.has(b.getAttribute('data-media-id'));

      if (aMatch !== bMatch) return aMatch ? -1 : 1;

      return Number(a.getAttribute('data-media-index') || 0) - Number(b.getAttribute('data-media-index') || 0);
    });

    orderedThumbs.forEach((thumb) => {
      if (!(thumb instanceof HTMLElement)) return;
      const isMatch = matchingIds.has(thumb.getAttribute('data-media-id'));
      thumb.hidden = !isMatch;
      thumb.style.display = isMatch ? '' : 'none';
      thumb.setAttribute('aria-hidden', String(!isMatch));
      thumb.classList.toggle('is-color-match', isMatch);
    });

    const currentOrder = thumbs.map((thumb) => thumb.getAttribute('data-media-id')).join(',');
    const nextOrder = orderedThumbs.map((thumb) => thumb.getAttribute('data-media-id')).join(',');
    if (currentOrder === nextOrder) {
      this.#syncMainMediaToVisibleRail();
      this.#refreshThumbnailControllers({ immediate: true });
      return;
    }

    const fragment = document.createDocumentFragment();
    orderedThumbs.forEach((thumb) => fragment.appendChild(thumb));
    railTrack.appendChild(fragment);
    this.#syncMainMediaToVisibleRail();
    this.#refreshThumbnailControllers({ immediate: true });
  }

  #syncDesktopRailToMediaId(mediaId) {
    const thumbSelector = `[data-rail-thumb][data-media-id="${CSS.escape(String(mediaId))}"]`;

    const applySync = () => {
      const rail = this.querySelector('[data-media-rail]');
      const viewport = rail?.querySelector('[data-rail-viewport]');
      const thumb = rail?.querySelector(thumbSelector);
      const allThumbs = rail ? Array.from(rail.querySelectorAll('[data-rail-thumb]')) : [];
      if (!(viewport instanceof HTMLElement) || !(thumb instanceof HTMLElement)) return;

      allThumbs.forEach((item) => {
        if (!(item instanceof HTMLElement)) return;
        const isActive = item === thumb;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-selected', String(isActive));
      });

      const viewportStyles = getComputedStyle(viewport);
      const track = rail?.querySelector('[data-rail-track]');
      const trackStyles = track instanceof HTMLElement ? getComputedStyle(track) : null;
      const safeInset =
        Number.parseFloat(viewportStyles.getPropertyValue('--desktop-rail-safe-inset')) ||
        Number.parseFloat(viewportStyles.paddingTop) ||
        0;
      const topButton = rail?.querySelector('[data-rail-up]');
      const topScrollOffset =
        topButton instanceof HTMLElement &&
        !topButton.hidden &&
        !topButton.disabled &&
        getComputedStyle(topButton).display !== 'none'
          ? topButton.offsetHeight + (Number.parseFloat(getComputedStyle(topButton).marginBottom || '0') || 0)
          : 0;
      const viewportTop = viewport.scrollTop + safeInset + topScrollOffset;
      const viewportBottom = viewport.scrollTop + viewport.clientHeight - safeInset;
      const thumbTop = thumb.offsetTop;
      const thumbBottom = thumbTop + thumb.offsetHeight;
      const maxScrollTop = Math.max(0, viewport.scrollHeight - viewport.clientHeight);

      let targetTop = null;
      if (thumbTop < viewportTop) {
        targetTop = thumbTop - safeInset - topScrollOffset;
      } else if (thumbBottom > viewportBottom) {
        targetTop = thumbBottom - viewport.clientHeight + safeInset;
      }

      if (targetTop !== null) {
        const trackGap = Number.parseFloat(trackStyles?.rowGap || trackStyles?.gap || '0') || 0;
        const rowHeight = thumb.offsetHeight + trackGap;
        if (rowHeight > 0) {
          const snapFn = targetTop < viewport.scrollTop ? Math.floor : Math.ceil;
          targetTop = snapFn(targetTop / rowHeight) * rowHeight;
        }
        const clampedTop = Math.max(0, Math.min(maxScrollTop, targetTop));
        if (Math.abs(clampedTop - viewport.scrollTop) > 1) {
          viewport.scrollTo({ top: clampedTop, behavior: 'auto' });
        }
      }
    };

    applySync();
    requestAnimationFrame(() => requestAnimationFrame(applySync));
  }

  #selectSlideshowMediaWithRetry(mediaId) {
    const targetId = String(mediaId);
    if (this.#isSlideshowAlreadyOnMediaId(targetId)) return;

    const attempts = [0, 16, 60, 140, 260];

    this.#clearSlideshowSelectTimeouts();
    attempts.forEach((delay) => {
      const timeout = window.setTimeout(() => {
        if (this.#isSlideshowAlreadyOnMediaId(targetId)) return;
        this.#forceSlideshowToMediaId(targetId);
        const slideshow = this.querySelector('slideshow-component');
        if (!slideshow?.select) return;
        slideshow.select({ id: targetId }, undefined, { animate: false });
      }, delay);
      this.#slideshowSelectTimeouts.push(timeout);
    });
  }

  #isSlideshowAlreadyOnMediaId(mediaId) {
    const targetId = String(mediaId || '');
    if (!targetId) return false;

    const slideshow = this.querySelector('slideshow-component');
    const scroller = slideshow?.querySelector('slideshow-slides[ref="scroller"], slideshow-slides');
    if (!(scroller instanceof HTMLElement)) return false;

    const slides = Array.from(scroller.querySelectorAll('slideshow-slide[data-media-id]')).filter(
      (slide) => slide instanceof HTMLElement && !slide.hidden && slide.style.display !== 'none'
    );
    const selectedSlide =
      slides.find(
        (slide) => slide.getAttribute('aria-hidden') !== 'true' && !slide.hasAttribute('data-slide-clone')
      ) ||
      slides.find((slide) => slide.getAttribute('aria-hidden') === 'false') ||
      slides[0];

    return selectedSlide instanceof HTMLElement && selectedSlide.getAttribute('data-media-id') === targetId;
  }

  #forceSlideshowToMediaId(mediaId) {
    const targetId = String(mediaId || '');
    if (!targetId) return;
    if (this.#isSlideshowAlreadyOnMediaId(targetId)) return;

    const slideshow = this.querySelector('slideshow-component');
    const scroller = slideshow?.querySelector('slideshow-slides[ref="scroller"], slideshow-slides');
    if (!(scroller instanceof HTMLElement)) return;

    const slides = Array.from(scroller.querySelectorAll('slideshow-slide[data-media-id]')).filter(
      (slide) => slide instanceof HTMLElement && !slide.hidden && slide.style.display !== 'none'
    );
    const targetSlide =
      slides.find(
        (slide) => !slide.hasAttribute('data-slide-clone') && slide.getAttribute('data-media-id') === targetId
      ) || slides.find((slide) => slide.getAttribute('data-media-id') === targetId);
    if (!(targetSlide instanceof HTMLElement)) return;

    if (slideshow instanceof HTMLElement) {
      slideshow.setAttribute('data-programmatic-media-sync', 'true');
      slideshow.setAttribute('data-programmatic-media-id', targetId);
      window.clearTimeout(slideshow.__programmaticMediaSyncRelease);
      slideshow.__programmaticMediaSyncRelease = window.setTimeout(() => {
        slideshow.removeAttribute('data-programmatic-media-sync');
        slideshow.removeAttribute('data-programmatic-media-id');
      }, 700);
    }

    slides.forEach((slide) => {
      slide.setAttribute('aria-hidden', String(slide !== targetSlide));
    });

    const paddingStart = Number.parseFloat(getComputedStyle(scroller).paddingInlineStart || '0') || 0;
    scroller.scrollTo({
      left: Math.max(0, targetSlide.offsetLeft - paddingStart),
      behavior: 'auto',
    });

    if (slideshow && typeof slideshow.current === 'number' && Array.isArray(slideshow.slides)) {
      const targetIndex = slideshow.slides.indexOf(targetSlide);
      if (targetIndex >= 0) {
        slideshow.current = targetIndex;
      }
    }

    this.#setMobileControlActiveMediaId(targetId);
  }

  #setMobileControlActiveMediaId(mediaId) {
    const targetId = String(mediaId || '');
    if (!targetId) return;

    this.querySelectorAll(
      '.media-gallery__mobile-controls .slideshow-control[data-media-id], .media-gallery__mobile-controls .slideshow-controls__dots > li[data-media-id]'
    ).forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      const isVisible = !node.hidden && node.style.display !== 'none' && node.getAttribute('aria-hidden') !== 'true';
      node.setAttribute('aria-selected', String(isVisible && node.getAttribute('data-media-id') === targetId));
    });
  }

  #clearSlideshowSelectTimeouts() {
    this.#slideshowSelectTimeouts.forEach((timeout) => window.clearTimeout(timeout));
    this.#slideshowSelectTimeouts = [];
  }

  #selectFirstVisibleSlideshowMediaWithRetry() {
    const resolveFirstVisibleMediaId = () => {
      const slides = Array.from(this.querySelectorAll('slideshow-slide[data-media-id]'));
      const isVisibleRealSlide = (slide) => {
        if (!(slide instanceof HTMLElement)) return false;
        return (
          !slide.hasAttribute('data-slide-clone') &&
          !slide.hidden &&
          slide.style.display !== 'none' &&
          slide.getAttribute('aria-hidden') !== 'true'
        );
      };
      const firstVisible = slides.find(isVisibleRealSlide);
      const firstSlide =
        firstVisible ||
        slides.find(
          (slide) =>
            slide instanceof HTMLElement &&
            !slide.hasAttribute('data-slide-clone') &&
            !slide.hidden &&
            slide.style.display !== 'none'
        ) ||
        slides.find((slide) => slide instanceof HTMLElement && !slide.hasAttribute('data-slide-clone')) ||
        slides.find((slide) => slide instanceof HTMLElement);
      return firstSlide instanceof HTMLElement ? firstSlide.getAttribute('data-media-id') : '';
    };

    const mediaId = resolveFirstVisibleMediaId();
    if (!mediaId) return;
    this.#selectSlideshowMediaWithRetry(mediaId);
    this.#syncDesktopRailToMediaId(mediaId);
  }

  #refreshThumbnailControllers({ immediate = false } = {}) {
    const refresh = () => {
      window.__mediaGalleryDesktopRail?.refresh?.(this);
      window.__mediaGalleryMobileThumbSync?.refresh?.(this);
    };

    if (immediate) {
      refresh();
      return;
    }

    requestAnimationFrame(refresh);
  }

  refreshThumbnailControllers(options) {
    this.#refreshThumbnailControllers(options);
  }

  /**
   * Called by bundle-picker's setActiveGalleryPart right after this gallery is
   * unhidden. First call runs the layout work that connectedCallback deferred
   * while the gallery was hidden (skeleton reveal, rail/thumb init, slide
   * select); later calls just re-sync the color-filtered selection.
   */
  activateBundleGallery() {
    if (!this.bundlePart) return;

    if (this.dataset.bundleActivated === 'true') {
      this.#syncSelectedColorMedia();
      return;
    }

    this.dataset.bundleActivated = 'true';
    this.#revealMediaWrapperWhenReady();
    this.#refreshThumbnailControllers({ immediate: true });
    this.#syncSelectedColorMedia();
  }

  #refreshZoomDialog(preferredMediaId = '') {
    const refresh = () => {
      this.refs.zoomDialogComponent?.refreshAfterMediaChange?.(preferredMediaId);
    };

    refresh();
    requestAnimationFrame(refresh);
  }

  // ===== Storefront-driven sibling swap =====

  /** Apply the already-requested parent data on a direct child PDP. */
  hydrateParentVideos(videos) {
    if (!this.hasAttribute('data-gallery-video-assignments') || this.dataset.galleryOwnVideos === 'true') return;
    const videoIds = videos.map((item) => String(item.id));
    const previousIds = (this.dataset.sharedVideoIds || '').split(',').filter(Boolean);
    if (videoIds.join(',') === previousIds.join(',')) return;
    const assigned = new Set([...previousIds, ...videoIds]);
    const nativeMedia = Array.from(this.querySelectorAll('slideshow-slide[data-media-id]:not([data-slide-clone])'))
      .filter((node) => !assigned.has(node.dataset.mediaId))
      .map((node) => ({
        id: node.dataset.mediaId,
        fileHandle: node.dataset.mediaFileHandle || '',
        altHandle: node.dataset.mediaAltHandle || '',
        alt: node.querySelector('img')?.alt || '',
        aspectRatio: Number(node.querySelector('.product-media')?.style.getPropertyValue('--ratio')) || null,
        preserveExisting: true,
      }));
    const media = nativeMedia.concat(videos);
    if (!this.canRebuildFromProductData(media)) return;
    this.rebuildFromProductData({ media, videoIds, imageColorGroup: this.dataset.imageColorGroup || '' });
    this.#syncSelectedColorMedia();
  }

  #applyVideoToContainer(node, item) {
    const template = this.querySelector('[data-gallery-video-template]');
    const productMedia = node.querySelector('.product-media');
    if (!(template instanceof HTMLTemplateElement) || !(productMedia instanceof HTMLElement)) return;
    node.classList.remove('product-media-container--image', 'product-media-container--model', 'product-media-container--external_video');
    node.classList.add('product-media-container--video');
    node.removeAttribute('on:click');
    node.querySelectorAll('.product-media-container__zoom-button').forEach((button) => button.remove());
    let player = productMedia.querySelector('deferred-media');
    if (!player || productMedia.dataset.mediaId !== String(item.id)) {
      productMedia.querySelectorAll('deferred-media, product-model, video-component, video, iframe, template').forEach((el) => el.remove());
      player = template.content.firstElementChild.cloneNode(true);
      const video = player.querySelector('template').content.querySelector('video');
      video.setAttribute('poster', item.src || '');
      video.setAttribute('aria-label', item.alt || '');
      item.videoSources.forEach((source) => {
        const element = document.createElement('source');
        element.src = source.url;
        element.type = source.mimeType;
        video.append(element);
      });
      productMedia.append(player);
    }
    player.pauseMedia?.();
    productMedia.dataset.mediaId = String(item.id);
    productMedia.style.setProperty('--ratio', String(item.aspectRatio || 1));
    node.style.setProperty('--media-preview-ratio', String(item.aspectRatio || 1));
    const label = template.dataset.loadLabel.replace('__description__', () => item.alt || '');
    player.querySelector('[ref="deferredMediaPlayButton"]')?.setAttribute('aria-label', label);
    player.querySelector('[ref="deferredMediaPlayButton"]')?.style.setProperty('--video-aspect-ratio', String(item.aspectRatio || 1));
    productMedia.querySelectorAll('img').forEach((img) => {
      img.src = item.src || '';
      img.alt = item.alt || '';
      img.removeAttribute('srcset');
      if (item.width) img.width = item.width;
      if (item.height) img.height = item.height;
    });
    const video = player.querySelector('video') || player.querySelector('template')?.content.querySelector('video');
    video?.setAttribute('aria-label', item.alt || '');
  }

  /**
   * Whether a clone-template rebuild can faithfully represent `media`.
   *
   * The rebuild can only clone regions that already exist, and the SSR gates
   * the desktop rail AND the whole slideshow-controls block (thumbnails, dots,
   * counter) on `sorted_media.size > 1` — see product-media-gallery-content
   * .liquid. Growing past one media therefore has no chrome to clone and would
   * render a carousel the shopper cannot page through. The caller falls back
   * to a full navigation instead.
   *
   * @param {Array<object>} media
   * @returns {boolean}
   */
  canRebuildFromProductData(media) {
    if (!Array.isArray(media) || media.length <= 1) return true;
    const slideCount = this.querySelectorAll(
      'slideshow-slide[data-media-id]:not([data-slide-clone])'
    ).length;
    if (slideCount > 1) return true;
    if (slideCount < 1) return false;

    return Boolean(
      this.querySelector(
        '[data-rail-track], .slideshow-controls, .media-gallery__mobile-controls'
      )
    );
  }

  /**
   * Rebuild every media region from Storefront product data — the PDP sibling
   * swap used to replace this gallery wholesale via a section-render morph;
   * now variant-picker-v2 hands over data and the existing SSR nodes act as
   * clone templates. Assigned gallery videos carry sources for playable
   * deferred media; other non-image media retains the existing poster fallback.
   * The <media-gallery> element itself is kept alive — its listeners and
   * controllers are bound for the section's life.
   *
   * @param {{
   *   media: Array<{ id: number|string, alt: string, fileHandle: string,
   *     altHandle: string, src: string, srcset: string, thumbSrc: string,
   *     zoomSrc: string, maxResolutionUrl: string, width: number|null,
   *     height: number|null, aspectRatio: number|null }>,
   *   productUrl?: string,
   *   imageColorGroup?: string,
   *   wishlist?: { empi?: string, iu?: string, dt?: string },
   *   videoIds?: string[],
   * }} data
   */
  rebuildFromProductData({ media, productUrl = '', imageColorGroup = '', wishlist, videoIds = [] } = {}) {
    if (!Array.isArray(media) || !media.length) return;

    this.dataset.sharedVideoIds = videoIds.join(',');
    this.#rebuildSlides(media);
    this.#rebuildGridItems(media);
    this.#rebuildRailThumbs(media);
    this.#rebuildMobileControls(media);
    this.#rebuildZoomDialog(media);

    // Media-count dependent in Liquid (`is_single_media`), so it has to be
    // recomputed or a former single-media product keeps single-media styling.
    this.#syncSingleMediaState(media.length);

    if (productUrl) this.dataset.galleryProductUrl = productUrl;
    if (imageColorGroup) {
      this.dataset.imageColorGroup = imageColorGroup;
    } else {
      delete this.dataset.imageColorGroup;
    }
    this.#updateWishlistToggles(wishlist, productUrl);

    // New nodes start unfiltered; the post-swap variant-picker:state-sync
    // re-applies the color filter against the new file handles.
    this.#explicitFeaturedMediaId = '';
    this.#explicitColorHandle = '';
    this.#setNoColorMediaMatchState(false);
    this.#refreshThumbnailControllers({ immediate: true });
    this.#refreshZoomDialog();

    // That sync lands a frame later, so the new colour flashed its whole set
    // before the length filter hid the other inseams. Apply it now, before
    // paint; the sync then re-applies the same result.
    const lengthMediaIds = this.#getLengthMatchingMediaIds();
    if (lengthMediaIds.length) this.#showOnlyMediaIds(lengthMediaIds);
  }

  /**
   * Clone `template` once per media item into its parent, replacing the
   * existing `nodes`. `update(clone, item, index)` mutates each clone.
   */
  #rebuildMediaCount = 0;

  #rebuildRegion(nodes, mediaItems, update) {
    this.#rebuildMediaCount = mediaItems.length;
    const template = nodes.find((node) => node instanceof HTMLElement && !node.querySelector('deferred-media')) || nodes[0];
    if (!(template instanceof HTMLElement) || !template.parentElement) return false;
    const parent = template.parentElement;
    let retainedIndex = -1;
    const clones = mediaItems.map((item, index) => {
      // Keep common players in place, avoiding a disconnectedCallback that
      // would cancel their playback listeners. Only retain an ordered subset.
      const match = nodes.findIndex((node) => node.getAttribute('data-media-id') === String(item.id));
      const retain = match > retainedIndex && (item.preserveExisting || item.videoSources?.length);
      if (retain) retainedIndex = match;
      const source = item.preserveExisting && match >= 0 ? nodes[match] : template;
      const clone = retain ? nodes[match] : source.cloneNode(true);
      this.#applyCommonMediaAttributes(clone, item, index);
      update(clone, item, index);
      return clone;
    });

    nodes.filter((node) => !clones.includes(node)).forEach((node) => {
      node.querySelectorAll('deferred-media').forEach((player) => player.pauseMedia?.());
      node.remove();
    });
    let anchor = null;
    for (const node of clones.slice().reverse()) {
      if (node.parentElement !== parent) parent.insertBefore(node, anchor);
      anchor = node;
    }
    return true;
  }

  #applyCommonMediaAttributes(node, item, index) {
    node.setAttribute('data-media-id', String(item.id));
    if (node.hasAttribute('data-media-index')) {
      node.setAttribute('data-media-index', String(index));
    }
    if (node.hasAttribute('data-media-file-handle')) {
      node.setAttribute('data-media-file-handle', item.fileHandle || '');
    }
    if (node.hasAttribute('data-media-alt-handle')) {
      node.setAttribute('data-media-alt-handle', item.altHandle || '');
    }
    const labels = this.querySelector('[data-gallery-video-template]')?.dataset;
    if (labels && node.matches('button, slideshow-slide')) {
      const label = node.closest('zoom-dialog') && item.alt
        ? labels.scrollLabel?.replace('__description__', () => item.alt)
        : labels.slideLabel?.replace('__index__', String(index + 1)).replace('__length__', String(this.#rebuildMediaCount));
      if (label) node.setAttribute('aria-label', label);
    }
    node.hidden = false;
    node.style.display = '';
    if (node.hasAttribute('aria-hidden')) node.setAttribute('aria-hidden', 'false');
    node.classList.remove('is-color-match');
    // Only the one template carried the view-transition tag; duplicating it
    // across every clone makes the browser skip the whole transition.
    if (index > 0) node.removeAttribute('data-view-transition-type');
  }

  /** Update an image or a playable assigned video in any gallery region. */
  #applyMediaToContainer(node, item, { eager = false, sizesOverride = '' } = {}) {
    if (item.preserveExisting) return;
    if (item.videoSources?.length) {
      this.#applyVideoToContainer(node, item);
      return;
    }
    node
      .querySelectorAll('deferred-media, product-model, video-component, video, iframe, .button-shopify-xr, template')
      .forEach((el) => el.remove());
    node.classList.remove(
      'product-media-container--video',
      'product-media-container--external_video',
      'product-media-container--model'
    );
    if (node.classList.contains('product-media-container') || node.matches('slideshow-slide, li')) {
      node.classList.add('product-media-container--image');
    }
    if (item.aspectRatio && node.style.getPropertyValue('--media-preview-ratio')) {
      node.style.setProperty('--media-preview-ratio', String(item.aspectRatio));
    }

    const productMedia = node.querySelector('.product-media');
    if (productMedia instanceof HTMLElement) {
      productMedia.setAttribute('data-media-id', String(item.id));
      if (item.aspectRatio) productMedia.style.setProperty('--ratio', String(item.aspectRatio));
    }

    const img = node.querySelector('img');
    if (img instanceof HTMLImageElement) {
      if (item.srcset) {
        img.setAttribute('srcset', item.srcset);
      } else {
        img.removeAttribute('srcset');
      }
      img.setAttribute('src', item.src);
      img.setAttribute('alt', item.alt || '');
      if (item.width) img.setAttribute('width', String(item.width));
      if (item.height) img.setAttribute('height', String(item.height));
      // snippets/product-media.liquid renders the zoom's high-res URL as
      // `data_max_resolution` (underscore) and zoom-dialog.js reads exactly
      // that attribute on select/open, replacing the <img> with it. Leaving it
      // stale here made the zoom dialog show the PREVIOUS sibling's photo after
      // a colour swap (right thumbnail, wrong 3840px image). Rewrite whichever
      // spelling the node carries.
      if (item.maxResolutionUrl) {
        if (img.hasAttribute('data_max_resolution')) {
          img.setAttribute('data_max_resolution', item.maxResolutionUrl);
        }
        if (img.hasAttribute('data-max-resolution')) {
          img.setAttribute('data-max-resolution', item.maxResolutionUrl);
        }
      }
      if (sizesOverride) img.setAttribute('sizes', sizesOverride);
      img.setAttribute('loading', eager ? 'eager' : 'lazy');
      if (eager) {
        img.setAttribute('fetchpriority', 'high');
      } else {
        img.removeAttribute('fetchpriority');
      }
    }
  }

  /** Plain thumbnail img (rail, mobile controls, zoom rail). */
  #applyMediaToThumbImage(node, item, src) {
    if (item.preserveExisting) return;
    node.querySelectorAll(
      '.media-gallery__thumbnail-badge, .slideshow-controls__thumbnail-badge'
    ).forEach((el) => el.remove());
    const img = node.querySelector('img');
    if (!(img instanceof HTMLImageElement)) return;
    img.removeAttribute('srcset');
    img.setAttribute('src', src || item.thumbSrc || item.src);
    img.setAttribute('alt', item.alt || '');
    if (item.width) img.setAttribute('width', String(item.width));
    if (item.height) img.setAttribute('height', String(item.height));
    img.setAttribute('loading', 'lazy');
    img.removeAttribute('fetchpriority');
    if (item.videoSources?.length) {
      const badge = document.createElement('span');
      badge.className = node.classList.contains('slideshow-controls__thumbnail')
        ? 'slideshow-controls__thumbnail-badge' : 'media-gallery__thumbnail-badge';
      const icon = this.querySelector('[data-gallery-video-template]')?.content.querySelector('.icon-play svg');
      if (icon) badge.append(icon.cloneNode(true));
      node.append(badge);
    }
  }

  /** Replace `/verb/<n>` indexes inside an `on:click`-style attribute. */
  #reindexActionAttribute(node, attribute, index) {
    const value = node.getAttribute(attribute);
    if (!value) return;
    node.setAttribute(attribute, value.replace(/\/(\d+)(?!.*\/\d)/, `/${index}`));
  }

  #rebuildSlides(media) {
    const scroller = this.querySelector('slideshow-slides[ref="scroller"], slideshow-slides');
    const allSlides = Array.from(this.querySelectorAll('slideshow-slide[data-media-id]'));
    if (!allSlides.length) return;
    // Loop clones carry stale media and are regenerated by the slideshow.
    const realSlides = allSlides.filter((slide) => !slide.hasAttribute('data-slide-clone'));
    const cloneSlides = allSlides.filter((slide) => slide.hasAttribute('data-slide-clone'));
    cloneSlides.forEach((slide) => slide.remove());

    // Every clone comes from one template slide, so markup Liquid renders on
    // only ONE slide would otherwise land on all of them. Two such conditions
    // exist (see product-media-gallery-content.liquid): the in-slide wishlist
    // toggle (`is_single_media and slide_is_selected`) and the `--tallest`
    // class (`lowest_aspect_ratio_index`). Re-derive both from the data.
    const isSingleMedia = media.length <= 1;
    const tallestIndex = this.#findTallestMediaIndex(media);

    const rebuilt = this.#rebuildRegion(realSlides, media, (slide, item, index) => {
      slide.setAttribute('aria-hidden', String(index !== 0));
      if (slide.hasAttribute('slide-id')) slide.setAttribute('slide-id', String(item.id));
      const style = slide.getAttribute('style') || '';
      if (style.includes('--slideshow-timeline')) {
        slide.setAttribute(
          'style',
          style.replace(/--slideshow-timeline:\s*--slide-\d+/, `--slideshow-timeline: --slide-${index}`)
        );
      }
      const zoomClick = slide.getAttribute('on:click');
      if (zoomClick && zoomClick.includes('/open/')) {
        slide.setAttribute('on:click', zoomClick.replace(/\/open\/\d+/, `/open/${index}`));
      }

      if (!isSingleMedia || index !== 0) {
        slide
          .querySelectorAll('.media-gallery__wishlist-toggle--in-slide')
          .forEach((toggle) => toggle.remove());
      }
      slide.classList.toggle('product-media-container--tallest', index === tallestIndex);

      this.#applyMediaToContainer(slide, item, { eager: index === 0 });
    });

    if (!rebuilt) return;

    if (scroller instanceof HTMLElement) {
      scroller.scrollTo({ left: 0, behavior: 'auto' });
    }
    // connectedCallback does not re-run for the slideshow, so it has to be
    // told its slides were replaced — otherwise its IntersectionObserver keeps
    // watching the nodes we just detached and scroll tracking dies.
    const slideshow = this.querySelector('slideshow-component');
    if (typeof slideshow?.refreshAfterSlidesReplaced === 'function') {
      slideshow.refreshAfterSlidesReplaced();
    }
  }

  /**
   * Index of the tallest media (lowest width/height), mirroring Liquid's
   * `lowest_aspect_ratio_index`. Returns -1 when no item reports a ratio.
   */
  #findTallestMediaIndex(media) {
    let index = -1;
    let lowest = Infinity;
    media.forEach((item, i) => {
      if (typeof item.aspectRatio !== 'number' || !item.aspectRatio) return;
      if (item.aspectRatio < lowest) {
        lowest = item.aspectRatio;
        index = i;
      }
    });
    return index;
  }

  #rebuildGridItems(media) {
    const grid = this.querySelector('.media-gallery__grid');
    if (!(grid instanceof HTMLElement)) return;
    const items = Array.from(grid.querySelectorAll(':scope > [data-media-id]'));
    this.#rebuildRegion(items, media, (item, mediaItem, index) => {
      const zoomButton = item.querySelector('.product-media-container__zoom-button');
      if (zoomButton) this.#reindexActionAttribute(zoomButton, 'on:click', index);
      this.#applyMediaToContainer(item, mediaItem, { eager: index === 0 });
    });
  }

  #rebuildRailThumbs(media) {
    const railTrack = this.querySelector('[data-rail-track]');
    if (!(railTrack instanceof HTMLElement)) return;
    const thumbs = Array.from(railTrack.querySelectorAll(':scope > [data-rail-thumb]'));
    this.#rebuildRegion(thumbs, media, (thumb, item, index) => {
      thumb.setAttribute('aria-selected', String(index === 0));
      thumb.classList.remove('is-active');
      if (item.aspectRatio && thumb.style.getPropertyValue('--aspect-ratio')) {
        thumb.style.setProperty('--aspect-ratio', String(item.aspectRatio));
      }
      this.#applyMediaToThumbImage(thumb, item, item.thumbSrc);
    });
  }

  #rebuildMobileControls(media) {
    // Thumbnail-style controls (may render for both desktop and mobile).
    this.querySelectorAll('.slideshow-controls__thumbnails').forEach((container) => {
      const thumbs = Array.from(container.querySelectorAll('.slideshow-controls__thumbnail[data-media-id]'));
      this.#rebuildRegion(thumbs, media, (thumb, item, index) => {
        thumb.setAttribute('aria-selected', String(index === 0));
        if (thumb.hasAttribute('data-index')) thumb.setAttribute('data-index', String(index));
        this.#reindexActionAttribute(thumb, 'on:click', index);
        if (item.aspectRatio && thumb.style.getPropertyValue('--aspect-ratio')) {
          thumb.style.setProperty('--aspect-ratio', String(item.aspectRatio));
        }
        this.#applyMediaToThumbImage(thumb, item, item.thumbSrc);
      });
    });

    // Dot-style controls.
    this.querySelectorAll('.slideshow-controls__dots').forEach((list) => {
      const dots = Array.from(list.querySelectorAll(':scope > li[data-media-id]'));
      this.#rebuildRegion(dots, media, (dot, item, index) => {
        const button = dot.querySelector('button');
        if (button) {
          if (button.hasAttribute('data-index')) button.setAttribute('data-index', String(index));
          this.#reindexActionAttribute(button, 'on:click', index);
          const style = button.getAttribute('style') || '';
          if (style.includes('--slide-')) {
            button.setAttribute('style', style.replace(/--slide-\d+/, `--slide-${index + 1}`));
          }
          button.setAttribute('aria-selected', String(index === 0));
          const label = this.querySelector('[data-gallery-video-template]')?.dataset.slideLabel;
          if (label) button.setAttribute('aria-label', label.replace('__index__', String(index + 1)).replace('__length__', String(media.length)));
          button.textContent = String(index + 1);
        }
      });
    });

    // Counter total ("1 / N"): the total is the text node after the slash.
    this.querySelectorAll('.slideshow-controls__counter').forEach((counter) => {
      const last = counter.lastChild;
      if (last && last.nodeType === Node.TEXT_NODE) {
        last.textContent = String(media.length);
      }
    });
  }

  #rebuildZoomDialog(media) {
    const zoomDialog = this.querySelector('zoom-dialog');
    if (!(zoomDialog instanceof HTMLElement)) return;

    const zoomThumbs = Array.from(
      zoomDialog.querySelectorAll('[ref="thumbnails"] > [data-media-id]')
    );
    this.#rebuildRegion(zoomThumbs, media, (thumb, item, index) => {
      if (index === 0) {
        thumb.setAttribute('aria-selected', 'true');
      } else {
        thumb.removeAttribute('aria-selected');
      }
      this.#reindexActionAttribute(thumb, 'on:click', index);
      this.#reindexActionAttribute(thumb, 'on:pointerenter', index);
      if (item.aspectRatio) {
        if (thumb.style.getPropertyValue('--aspect-ratio')) {
          thumb.style.setProperty('--aspect-ratio', String(item.aspectRatio));
        }
        if (thumb.style.getPropertyValue('--gallery-aspect-ratio')) {
          thumb.style.setProperty('--gallery-aspect-ratio', String(item.aspectRatio));
        }
      }
      this.#applyMediaToThumbImage(thumb, item, item.zoomSrc);
    });

    const zoomItems = Array.from(
      zoomDialog.querySelectorAll('.dialog-zoomed-gallery > [data-media-id]')
    );
    this.#rebuildRegion(zoomItems, media, (item, mediaItem, index) => {
      if (item.id) item.id = `product-${mediaItem.id}-${index + 1}`;
      this.#applyMediaToContainer(item, mediaItem, { sizesOverride: '100vw' });
    });
  }

  #updateWishlistToggles(wishlist, productUrl) {
    if (!wishlist) return;
    this.querySelectorAll('[data-wishlist-toggle]').forEach((toggle) => {
      if (!(toggle instanceof HTMLElement)) return;
      if (wishlist.empi) toggle.setAttribute('data-wishlist-empi', wishlist.empi);
      if (wishlist.iu) toggle.setAttribute('data-wishlist-iu', wishlist.iu);
      if (wishlist.dt) toggle.setAttribute('data-wishlist-dt', wishlist.dt);
      // Keep the SSR origin (shop primary domain) and swap only the path.
      const du = toggle.getAttribute('data-wishlist-du');
      if (du && productUrl) {
        try {
          const url = new URL(du);
          url.pathname = productUrl;
          url.search = '';
          toggle.setAttribute('data-wishlist-du', url.toString());
        } catch {
          /* leave the old deep link */
        }
      }
      // The variant id is product-scoped state the reinit resolves afresh.
      toggle.setAttribute('data-wishlist-epi', '');
      toggle.setAttribute('aria-pressed', 'false');
    });
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
